/**
 * The admin's "Remove all devices" on a founder's box card (board onboarding-r2): one row per Linux user on the live
 * box, who is on it, when its devices were last removed, and the button. It re-keys that user's Paseo, so everyone on
 * it pairs again from their dashboard; the admin never sees their new link.
 */
import { useState } from 'react'
import { boxUserRows, removeQuestionFor, rotateProblem, type BoxUserRow } from '../../domain/devices'
import { ago } from '../../domain/format'
import type { PersonView } from '../../domain/schemas'
import type { Api } from '../../infra/api'
import { ConfirmAction, DataTable, EmptyState, ErrorNote, Stack, Text } from '../../ui'

export function BoxDevices({ api, founderId, founderUser, people, onDone }: { api: Api; founderId: string; founderUser: string | null; people: PersonView[]; onDone: () => void }) {
  const [working, setWorking] = useState<string | null>(null)
  const [result, setResult] = useState<{ user: string; ok: boolean; words: string } | null>(null)
  const rows = boxUserRows(people, founderUser)

  async function remove(user: string) {
    setWorking(user)
    setResult(null)
    try {
      const r = await api.rotatePairing(user, founderId)
      setResult({ user, ok: true, words: `Devices removed from ${r.user}. ${r.people === 1 ? 'Its person pairs' : `All ${r.people} people on it pair`} again from the dashboard.` })
      onDone()
    } catch (e) {
      setResult({ user, ok: false, words: rotateProblem(e) })
    } finally {
      setWorking(null)
    }
  }

  return (
    <Stack gap="sm">
      <DataTable<BoxUserRow>
        label="Paired devices by Linux user"
        framed={false}
        rows={rows}
        rowKey={(r) => r.user}
        empty={<EmptyState title="No one on the box yet.">The box step and Add person record who works as which Linux user.</EmptyState>}
        columns={[
          { key: 'user', label: 'Linux user', narrow: 'main', render: (r) => <Text mono weight="head">{r.user}</Text> },
          { key: 'people', label: 'People', render: (r) => <Text size="sm">{r.people.join(', ')}</Text> },
          { key: 'removed', label: 'Devices removed', render: (r) => <Text size="sm" tone="muted">{r.removedAt ? ago(r.removedAt) : 'never'}</Text> },
          {
            key: 'remove',
            label: 'Devices',
            narrowLabel: false,
            render: (r) => (
              <ConfirmAction
                label="Remove all devices"
                strength="confirm"
                yes="Remove them all"
                variant="line"
                question={removeQuestionFor(r.user, r.people)}
                working={working === r.user}
                disabled={working !== null && working !== r.user}
                onConfirm={() => void remove(r.user)}
              >
                {result?.user === r.user && result.ok ? <Text size="sm">{result.words}</Text> : null}
              </ConfirmAction>
            ),
          },
        ]}
      />
      {result && !result.ok && <ErrorNote title={`Devices on ${result.user} weren't removed.`} reason={result.words} />}
    </Stack>
  )
}
